import React from 'react';
import { getCurrentWeek, getTotalWeeks } from './clientUtils';
import { Badge, EmptyState } from './components/primitives';
import { NEUTRAL, STATUS_COLORS } from './designTokens';

function ClientProgramsTab({
  programs,
  openProgram,
  weekSessionCounts,
  brandAccent,
  brandTint,
  brandTintBorder,
  theme,
}) {
  if (programs.length === 0) {
    return (
      <div style={{ padding: '56px 24px 64px' }}>
        <EmptyState
          theme={theme}
          icon={
            <div style={{
              width: '56px', height: '56px',
              background: brandTint,
              borderRadius: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={brandAccent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
                <path d="M9 12h6M9 16h4"/>
              </svg>
            </div>
          }
          title="Ditt konto är klart!"
          message="Din terapeut håller på att lägga upp ditt program. Det dyker upp här så snart det är klart."
        />
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const activePrograms = programs.filter(cp => !cp.end_date || cp.end_date >= today);
  const endedPrograms = programs.filter(cp => cp.end_date && cp.end_date < today);

  const renderProgramCard = (cp) => {
    const currentWeek = getCurrentWeek(cp);
    const totalWeeks = getTotalWeeks(cp);
    const weekProgress = totalWeeks ? Math.min(100, Math.round((currentWeek / totalWeeks) * 100)) : null;
    const isNew = !cp.client_seen;

    return (
      <div
        key={cp.id}
        onClick={() => openProgram(cp)}
        style={{
          background: isNew ? brandTint : 'white',
          border: `1px solid ${isNew ? brandTintBorder : NEUTRAL.line}`,
          borderRadius: '14px',
          padding: '18px 20px',
          marginBottom: '10px',
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '1.03em', color: NEUTRAL.ink }}>
                {cp.program?.title}
              </strong>
              {isNew && <Badge theme={theme} variant="accent">Nytt</Badge>}
            </div>
            {cp.program?.description && (
              <p style={{ margin: '0 0 10px', color: NEUTRAL.muted, fontSize: '0.875em', lineHeight: '1.45' }}>
                {cp.program.description}
              </p>
            )}
            {cp.start_date && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Badge theme={theme} variant="accent">
                  Vecka {currentWeek}{totalWeeks ? ` av ${totalWeeks}` : ''}
                </Badge>
                {cp.frequency_per_week > 0 && (
                  <span style={{ color: NEUTRAL.faint, fontSize: '0.8em' }}>
                    {weekSessionCounts[cp.program?.id] || 0} av {cp.frequency_per_week} pass
                  </span>
                )}
              </div>
            )}
            {weekProgress !== null && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ background: NEUTRAL.line, borderRadius: '4px', height: '5px' }}>
                  <div style={{ background: brandAccent, width: `${weekProgress}%`, height: '5px', borderRadius: '4px' }} />
                </div>
              </div>
            )}
          </div>
          <span style={{ color: NEUTRAL.faint, marginLeft: '14px', flexShrink: 0, fontSize: '1.05em' }}>→</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      {activePrograms.map(renderProgramCard)}
      {endedPrograms.length > 0 && (
        <div style={{ marginTop: activePrograms.length > 0 ? '28px' : '0' }}>
          <p style={{ margin: '0 0 10px', fontSize: '0.78em', color: STATUS_COLORS.neutral, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Avslutade program
          </p>
          {endedPrograms.map(cp => (
            <div
              key={cp.id}
              onClick={() => openProgram(cp)}
              style={{
                background: NEUTRAL.page,
                border: `1px solid ${NEUTRAL.line}`,
                borderRadius: '14px',
                padding: '16px 20px',
                marginBottom: '8px',
                cursor: 'pointer',
                opacity: 0.7,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: '0.97em', color: NEUTRAL.text }}>{cp.program?.title}</strong>
                  {cp.end_date && (
                    <div style={{ fontSize: '0.8em', color: NEUTRAL.faint, marginTop: '3px' }}>
                      Avslutades {cp.end_date}
                    </div>
                  )}
                </div>
                <span style={{ color: NEUTRAL.faint, marginLeft: '14px', flexShrink: 0, fontSize: '1em' }}>→</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClientProgramsTab;
